import TransformComponent from '../../Component/Transform';
import Renderable from '../../Component/Renderable';
import GameObject from '../../GameObject/GameObject';
import Game from '../../Game';
import FieldComponent from '../../Component/Map/Field';
import { IFieldData, GrowthStage } from '../../State/World/IFieldData';
import { PlayerActions } from '../../State/Player/PlayerActions';
import Crop from './Crop';
import { StartAnimationClip } from '../../System/Animation/AnimationHelper';
import CropComponent from '../../Component/Map/Crop';
import { Random } from '../../Utility/Random';
import Point from '../../Utility/Point';
import { AnimationClipEvent } from '../../System/Animation/AnimationClip';
import { PlaySound } from '../../System/Audio/AudioHelper';
import { AudioTrackType } from '../../System/Audio/AudioClip';
import PIXIRenderer from '../../System/Render/PIXIRenderer';
import Sign from './Sign';
import { IGameState } from '../../State/Game/IGameState';
import { FieldStateHelper } from 'State/Player/Fields/FieldStateHelper';
import Sprite from '../Sprite';
import { CropTransition, FieldHarvest } from 'System/Animation/Clip';
import CoinBar from 'Prefabs/UI/HUD/CoinBar';
import Container from 'Prototype/Container';
import { getFieldSectorId } from 'System/Field/FieldHelper';

export interface SectorLockData {
    locked: boolean;
    newUnlock?: boolean;
}

export enum FieldEvent {
    UNLOCK = 'unlock_field',
}

export const CropParticles: Random.IWeightedItem<string>[] = [
    { odds: 0.33, data: 'crop_coin' },
    { odds: 0.11, data: 'crop_shine_1' },
    { odds: 0.11, data: 'crop_shine_2' },
    { odds: 0.11, data: 'crop_shine_3' },
];

export default class Field extends GameObject {
    public worldNumber: number;
    private worldLayout: any;
    private fieldIndex: number;

    public onCreate(params: {
        worldLayout: any;
        fieldIndex: number;
        fieldData: IFieldData;
        worldNumber: number;
        signBubblesContainer: Container;
    }): void {
        this.name = `${params.fieldData.id}_field`;
        this.worldNumber = params.worldNumber;
        this.worldLayout = params.worldLayout;
        this.fieldIndex = params.fieldIndex;

        // Check if locked and add listener for unlock if applicable
        const { fieldLocked, sectorLockData } = Field.getFieldUnlockedData(params.fieldData);

        // Add Components
        this.addComponent<Renderable>(Renderable);
        this.addComponent<TransformComponent>(TransformComponent, {
            x: 0,
            y: 0,
        });

        const fieldComponent: FieldComponent = this.addComponent<FieldComponent>(FieldComponent, {
            ...params.fieldData,
            locked: fieldLocked,
            harvestSettings: params.fieldData.cropSettings.harvestSettings,
            sectorLockData,
        });

        // If field is not active in this world then return
        if (!this.isActiveWorld) return;

        this.setEventListeners();
        this.createCrops(fieldComponent, params.fieldData, sectorLockData);
        this.createSigns(fieldComponent, params.fieldData, sectorLockData, params.signBubblesContainer);
    }

    protected createSigns(
        fieldComponent: FieldComponent,
        fieldData: IFieldData,
        sectorLockData: SectorLockData[],
        signBubblesContainer: Container
    ): void {
        const signs: Sign[] = [];

        [this.worldLayout.signsSectorA[this.fieldIndex], this.worldLayout.signsSectorB[this.fieldIndex]].forEach(
            (sector, sectorIndex) => {
                [...sector.children].forEach((baseSprite: Sprite) => {
                    const sign: Sign = GameObject.Instantiate(Sign, {
                        baseSprite,
                        sector: sectorIndex,
                        unlockLevel: fieldData.requiredLevel[sectorIndex],
                        visible: sectorLockData[sectorIndex].locked,
                        signBubblesContainer,
                    });

                    this.addChild(sign);
                    signs.push(sign);
                });
            }
        );
        fieldComponent.signs = signs;
    }

    protected createCrops(
        fieldComponent: FieldComponent,
        fieldData: IFieldData,
        sectorLockData: SectorLockData[]
    ): void {
        const growthStage: GrowthStage = fieldComponent.getGrowthStage();
        const crops: Crop[] = [];

        [this.worldLayout.cropsSectorA[this.fieldIndex], this.worldLayout.cropsSectorB[this.fieldIndex]].forEach(
            (sector, sectorIndex) => {
                const sortedCropSprites = sector.children
                    .slice()
                    .sort(
                        (a: GameObject, b: GameObject) =>
                            a.getComponent(TransformComponent).y - b.getComponent(TransformComponent).y
                    );

                sortedCropSprites.forEach((cropSprite: Sprite) => {
                    const currentStage: GrowthStage = sectorLockData[sectorIndex].locked
                        ? GrowthStage.LOCKED
                        : growthStage;

                    const newCrop: Crop = GameObject.Instantiate(Crop, {
                        cropSprite,
                        currentStage,
                        textures: fieldData.growthStages,
                        textureOverrides: cropSprite.layoutData
                            ? (cropSprite.layoutData as { defaultTextures: [] }).defaultTextures
                            : null,
                        base: fieldData.cropSettings.base,
                        lockData: sectorLockData[sectorIndex],
                        sector: sectorIndex,
                    });

                    fieldComponent.addToSector(newCrop, sectorIndex);

                    crops.push(newCrop);
                    sector.addChild(newCrop);
                });
            }
        );

        fieldComponent.crops = crops;
    }

    protected updateCrops(): void {
        if (!this.isActiveWorld) return;

        const fieldComponent = this.getComponent(FieldComponent);

        if (fieldComponent.locked) return;

        const state: IGameState = Game.store.getState();
        const stage: GrowthStage = fieldComponent.getGrowthStage(state);

        if (fieldComponent.currentStage === stage) return;
        fieldComponent.currentStage = stage;

        let cropsTransitioned = 0;
        let totalCrops: number = 0;

        const sectorIsLocked: boolean[] = fieldComponent.requiredLevel.map((requirement: number, sectorId: number) => {
            const isLocked: boolean = fieldComponent.isSectorLocked(state, sectorId);

            if (!isLocked && fieldComponent.sectors.has(sectorId)) {
                totalCrops += fieldComponent.sectors.get(sectorId).length - 1;
            }

            return isLocked;
        });

        PlaySound('harvest-transition');

        fieldComponent.crops.forEach((crop: Crop) => {
            const cropComponent = crop.getComponent(CropComponent);

            if (sectorIsLocked[cropComponent.sector]) return;

            const cropTransition = new CropTransition(crop, {
                amplitude: 0.25,
                duration: 275,
                stage,
            });

            cropTransition.on(AnimationClipEvent.COMPLETE, () => {
                cropsTransitioned++;

                if (cropsTransitioned === totalCrops && FieldStateHelper.canHarvest(state)) {
                    this.scene.emit('EnableHarvestButton');
                }
            });

            StartAnimationClip(cropTransition);
        });
    }

    protected harvestCrops(params: { previousCoins: number; income: number }): void {
        if (!this.isActiveWorld) return;

        const fieldComponent = this.getComponent(FieldComponent);

        if (fieldComponent.locked) return;

        const state: IGameState = Game.store.getState();

        const unlockedCrops: Crop[] = fieldComponent.crops.filter(
            (crop: Crop) => !fieldComponent.isSectorLocked(state, crop.getComponent(CropComponent).sector)
        );

        const growthStage: GrowthStage = fieldComponent.getGrowthStage(state);
        fieldComponent.currentStage = growthStage;

        const destination: Point = Field.getDestinationPoint();

        StartAnimationClip(
            new FieldHarvest(this, {
                rewardTextures: [...fieldComponent.harvestSettings.icons, ...CropParticles],
                destination,
                unlockedCrops,
                previousCoins: params.previousCoins,
                income: params.income,
            })
        );
    }

    protected setEventListeners(): void {
        const scene = Game.scene.getActiveScene();

        scene.on('TransitionCrops', this.updateCrops.bind(this));
        scene.on('HarvestCrops', this.harvestCrops.bind(this));
    }

    public static getFieldUnlockedData(
        fieldData: IFieldData
    ): {
        sectorLockData: SectorLockData[];
        fieldLocked: boolean;
    } {
        let fieldLocked: boolean = true;
        const state: IGameState = Game.store.getState();

        const sectorLockData: SectorLockData[] = fieldData.requiredLevel.map(
            (requirement: number, sectorId: number) => {
                const id = getFieldSectorId(fieldData.id, sectorId);
                const locked = Game.player.getHighestLevelCompleted(state) < requirement;

                if (!locked) {
                    fieldLocked = false;
                }

                const alreadyUnlocked: boolean = state.playerState.fields.indexOf(id) !== -1;

                if (!alreadyUnlocked && !locked) {
                    Field.setUnlockListener(id);

                    return {
                        locked,
                        newUnlock: true,
                    };
                } else {
                    return {
                        locked,
                    };
                }
            }
        );

        return {
            fieldLocked,
            sectorLockData,
        };
    }

    public static setUnlockListener(id: string): void {
        const scene = Game.scene.getActiveScene();

        scene.once(FieldEvent.UNLOCK, () => {
            PlayerActions.unlockFields(id);
        });
    }

    public static getDestinationPoint(): Point {
        const destinationObject: GameObject = GameObject.findFirstByName(CoinBar.BUTTON_NAME);

        if (destinationObject === undefined) {
            console.error('Destination element not found');
            return;
        }

        const position: PIXI.Point = destinationObject.container.getGlobalPosition();
        return new Point(position.x, position.y);
    }

    public update(): void {
        if (!this.isActiveWorld) return;

        const nodePadding = [170, 170, 170, 170];
        const crops: Crop[] = this.getComponent(FieldComponent).crops;

        crops.forEach(crop => {
            if (crop.destroyed) return;

            const position = crop.container.toGlobal(new Point());

            crop.container.visible =
                position.x + nodePadding[0] >= 0 && position.x - nodePadding[2] <= PIXIRenderer.renderer.width;
        });
    }

    public get isActiveWorld(): boolean {
        const state: IGameState = Game.store.getState();
        return state.worldState.worldNumber === this.worldNumber;
    }
}
